import React from 'react';
import { EyeOutlined, FilePdfOutlined, FileProtectOutlined, SaveOutlined, DollarOutlined, SolutionOutlined, UserOutlined, BankOutlined, SyncOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Form, Input, InputNumber, Select, Radio, Button, Card, Row, Col, Typography, Divider, Spin, Space, Switch, Breadcrumb, Alert, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../interfaces/Global';
import { useCadastrarProposta, getCidadeUf } from './useCadastrarProposta';
import { VisualizadorPropostaModal, PropostaPrintDocument } from '../components';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
};

export const normalizarTexto = (texto: any): string => {
    return String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
};

const CadastrarProposta: React.FC = () => {
    const isDark = useSelector((state: stateGlobal) => state?.global?.theme === 'dark');
    const {
        form,
        isEdit,
        loading,
        loadingInicial,
        loadingPlanos,
        loadingConfigGeral,
        submitting,
        configPlanos,
        selectedConfigPlano,
        quantidadeLicencas,
        quantidadeHoras,
        valorUnitarioPlano,
        valorTotalLicencas,
        contratacaoHoras,
        valorUnitarioHora,
        valorTotalHoras,
        valorTotalGeral,
        condicaoPagamento,
        numeroParcelas,
        valorParcela,
        previewVisible,
        setPreviewVisible,
        handleOpenPreview,
        getPreviewData,
        setQuantidadeLicencas,
        setQuantidadeHoras,
        handlePlanoChange,
        selectedUf,
        handleUfChange,
        handleCidadeSelect,
        cidadesFiltradas,
        ESTADOS_BRASIL,
        cidades,
        loadingCidades,
        usuarios,
        loadingUsuarios,
        loggedUser,
        configGeral,
        desabilitarSelecaoMunicipio,
        handleToggleDesabilitarMunicipio,
        onFinish,
        handleFormChange,
        houveMudancaPlano,
        mudancasPlano,
        alertaMudancaFechado,
        handleAtualizarComPlanoVigente,
        handleDescartarAvisoMudanca,
        propostaSalva,
        propostaParaDownload,
        history,
        printRef
    } = useCadastrarProposta();

    const [isMobile, setIsMobile] = React.useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 768);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const cidadeSelecionada = Form.useWatch('cidade_nome', form);
    const tituloCabecalhoDesabilitado = desabilitarSelecaoMunicipio
        ? !cidadeSelecionada
        : (!selectedUf || !cidadeSelecionada);

    if (loadingInicial) {
        return (
            <div style={{ textAlign: 'center', padding: '120px 0' }}>
                <Spin size="large" tip={isEdit ? "Carregando dados da proposta..." : "Carregando formulário de proposta..."} />
            </div>
        );
    }

    return (
        <div style={{ padding: isMobile ? '12px 8px' : '24px' }}>
            {/* Header com Breadcrumb */}
            <div style={{ marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0, marginBottom: 6 }}>
                    <FileProtectOutlined style={{ marginRight: 8 }} />
                    {isEdit ? 'Editar Proposta Comercial' : 'Cadastrar Nova Proposta Comercial'}
                </Title>
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <a onClick={() => history.push('/propostas-comerciais')}>Propostas Comerciais</a>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        {isEdit ? 'Editar Proposta Comercial' : 'Cadastrar Proposta Comercial'}
                    </Breadcrumb.Item>
                </Breadcrumb>
                <Divider style={{ margin: '14px 0 20px 0' }} />
            </div>

            {/* Alerta de Mudança na Configuração do Plano */}
            {houveMudancaPlano && !alertaMudancaFechado && !propostaSalva && (
                <Alert
                    message={
                        <Space align="center">
                            <ExclamationCircleOutlined style={{ fontSize: 18, color: '#faad14' }} />
                            <Text strong style={{ fontSize: 15 }}>
                                O modelo de plano vinculado a esta proposta foi alterado no sistema
                            </Text>
                        </Space>
                    }
                    description={
                        <div style={{ marginTop: 8 }}>
                            <p style={{ margin: 0, marginBottom: 8, fontSize: 13 }}>
                                Esta proposta foi gerada/duplicada com valores e textos congelados de sua versão original. Identificamos as seguintes divergências com a configuração atual do plano (<strong>{selectedConfigPlano?.plano?.nome || 'Plano'}</strong>):
                            </p>
                            <ul style={{ margin: 0, paddingLeft: 20, marginBottom: 14, fontSize: 13 }}>
                                {mudancasPlano.map((m, idx) => (
                                    <li key={idx} style={{ marginBottom: 4 }}>{m}</li>
                                ))}
                            </ul>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                <Space wrap>
                                    <Button onClick={handleDescartarAvisoMudanca}>
                                        Manter com os valores antígos
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<SyncOutlined />}
                                        onClick={handleAtualizarComPlanoVigente}
                                        style={{
                                            background: '#0066b2',
                                            borderColor: '#0066b2',
                                            fontWeight: 600
                                        }}
                                    >
                                        Atualizar com as Configurações Vigentes
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    }
                    type="warning"
                    showIcon={false}
                    closable
                    onClose={handleDescartarAvisoMudanca}
                    style={{
                        marginBottom: 20,
                        borderRadius: 8,
                        border: '1px solid #faad14',
                        // border: '1px solid transparent',
                        background: isDark ? '#2b2111' : '#fffbe6'
                    }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={handleFormChange}
                initialValues={{
                    titulo_cabecalho: 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                    vendedor_id: loggedUser?.id,
                    contato_principal_id: configGeral?.contato_padrao_id,
                    quantidade_licencas: 1,
                    quantidade_horas: 0,
                    condicao_pagamento: 'parcela_unica'
                }}
            >
                {/* Campos Ocultos para Sincronização */}
                {!desabilitarSelecaoMunicipio && (
                    <Form.Item name="cidade_nome" hidden>
                        <Input />
                    </Form.Item>
                )}
                <Form.Item name="cidade_uf" hidden>
                    <Input />
                </Form.Item>

                <Row gutter={24}>
                    {/* Coluna Esquerda: Formulário de Dados */}
                    <Col xs={24} lg={16}>
                        {/* Bloco 1: Município e Título da Proposta */}
                        <Card
                            type="inner"
                            title={<><BankOutlined /> Identificação</>}
                            style={{ marginBottom: 20, borderRadius: '8px' }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 16,
                                }}
                            >
                                <Text strong>Desabilitar seleção de município</Text>

                                <Switch
                                    size="small"
                                    // checkedChildren="Ativo"
                                    // unCheckedChildren="Inativo"
                                    checked={desabilitarSelecaoMunicipio}
                                    onChange={handleToggleDesabilitarMunicipio}
                                    style={{
                                        minWidth: 32,
                                    }}
                                />
                            </div>

                            <Form.Item
                                name="titulo_cabecalho"
                                label="Título do Cabeçalho da Proposta"
                                rules={[{ required: true, message: 'Por favor, informe o título do cabeçalho' }]}
                            // tooltip={tituloCabecalhoDesabilitado ? "Informe ou selecione uma Cidade/Identificação primeiro" : undefined}
                            >
                                <Input
                                    placeholder="Ex: PROPOSTA COMERCIAL AO MUNICÍPIO DE"
                                // disabled={tituloCabecalhoDesabilitado}
                                />
                            </Form.Item>

                            {desabilitarSelecaoMunicipio ? (
                                <Row gutter={16}>
                                    <Col xs={24}>
                                        <Form.Item
                                            name="cidade_nome"
                                            label="Associação / Destinatário"
                                            rules={[{ required: true, message: 'Por favor, informe a identificação ou município' }]}
                                        >
                                            <Input
                                                placeholder="Ex: CONSÓRCIO INTERMUNICIPAL DE SAÚDE ou CIDADE EXEMPLO"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ) : (
                                <Row gutter={16}>
                                    <Col xs={24}>
                                        <Form.Item
                                            name="cidade_id"
                                            label="Município"
                                            rules={[{ required: true, message: 'Selecione o município' }]}
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                loading={loadingCidades}
                                                placeholder="Digite para buscar qualquer município do Brasil..."
                                                optionFilterProp="children"
                                                filterOption={(input, option: any) =>
                                                    normalizarTexto(option?.children).includes(normalizarTexto(input))
                                                }
                                                onChange={handleCidadeSelect}
                                                notFoundContent={loadingCidades ? "Carregando municípios..." : "Nenhum município encontrado"}
                                            >
                                                {cidades.map(c => {
                                                    const uf = getCidadeUf(c);
                                                    const label = uf ? `${c.nome} - ${uf}` : c.nome;
                                                    return (
                                                        <Option key={c.id} value={c.id}>
                                                            {label}
                                                        </Option>
                                                    );
                                                })}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}
                        </Card>
                        {/* Bloco 2: Plano Comercial */}
                        <Card
                            type="inner"
                            title={<><DollarOutlined /> Plano e Licenciamento</>}
                            style={{ marginBottom: 20, borderRadius: '8px' }}
                        >
                            <Form.Item
                                name="config_plano_proposta_comercial_id"
                                label="Selecione o Modelo de Plano"
                                rules={[{ required: true, message: 'Selecione um plano comercial' }]}
                            >
                                <Select
                                    placeholder="Selecione um modelo de plano configurado"
                                    onChange={handlePlanoChange}
                                    loading={loadingPlanos}
                                >
                                    {configPlanos.map(item => (
                                        <Option key={item.id} value={item.id}>
                                            {/* {item.is_principal ? '★ ' : ''} */}
                                            {item.plano?.nome || item.descricao_plano || `Plano #${item.id}`} - {formatarMoeda(Number(item.plano?.valor || 0))}
                                            {/* {item.is_principal ? ' (Principal)' : ''} */}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="quantidade_licencas"
                                        label="Quantidade de Licenças"
                                        rules={[{ required: true, message: 'Informe a quantidade de licenças' }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            style={{ width: '100%' }}
                                            value={quantidadeLicencas}
                                            onChange={(val) => {
                                                const n = Number(val) || 1;
                                                setQuantidadeLicencas(n);
                                                form.setFieldsValue({ quantidade_licencas: n });
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                {contratacaoHoras && (
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="quantidade_horas"
                                            label={`Horas` /*de Consultoria / Suporte (${formatarMoeda(valorUnitarioHora)}/h)*/}
                                        >
                                            <InputNumber
                                                min={0}
                                                style={{ width: '100%' }}
                                                value={quantidadeHoras}
                                                onChange={(val) => {
                                                    const n = Number(val) || 0;
                                                    setQuantidadeHoras(n);
                                                    form.setFieldsValue({ quantidade_horas: n });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>

                            <Form.Item
                                name="condicao_pagamento"
                                label="Forma de Faturamento"
                                rules={[{ required: true, message: 'Selecione a forma de faturamento' }]}
                            >
                                <Radio.Group style={{ width: '100%' }}>
                                    <Space wrap size={[24, 10]} style={{ width: '100%' }}>
                                        <Radio value="parcela_unica">Parcela Única (Anual / À vista)</Radio>
                                        <Radio value="parcelado">Parcelado (Mensal)</Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>

                            {condicaoPagamento === 'parcelado' && (
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="numero_parcelas"
                                            label="Quantidade de Parcelas"
                                            rules={[{ required: true, message: 'Informe a quantidade de parcelas' }]}
                                            tooltip="Informe o número de parcelas mensais (padrão: 12x)"
                                        >
                                            <InputNumber
                                                min={1}
                                                max={60}
                                                style={{ width: '100%' }}
                                                addonAfter="x"
                                                placeholder="12"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Valor Calculado por Parcela (Licenciamento)">
                                            <Input
                                                readOnly
                                                disabled
                                                value={`${Number(numeroParcelas || 12)}x de ${formatarMoeda(valorParcela)}`}
                                                style={{
                                                    color: '#1890ff',
                                                    fontWeight: 600,
                                                    backgroundColor: isDark ? '#1f1f1f' : '#f0f5ff',
                                                    borderColor: '#91d5ff'
                                                }}
                                            />
                                            {contratacaoHoras && quantidadeHoras > 0 && (
                                                <Text type="secondary" style={{ fontSize: '11px', marginTop: 4, display: 'block' }}>
                                                    * Calculado sobre o licenciamento. Horas técnicas são faturadas sob demanda à parte.
                                                </Text>
                                            )}
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}

                            <Form.Item
                                name="forma_pagamento_descricao"
                                label="Detalhes de Pagamento"
                                rules={[{ required: true, message: 'Informe os detalhes da forma de pagamento' }]}
                            >
                                <TextArea
                                    rows={5}
                                    placeholder="Ex: Pagamento via empenho/boleto bancário..."
                                />
                            </Form.Item>

                            {contratacaoHoras && quantidadeHoras > 0 && (
                                <Form.Item
                                    name="detalhamento_horas"
                                    label="Detalhamento da Contratação de Horas"
                                    rules={[{ required: true, message: 'Informe o detalhamento da contratação de horas' }]}
                                    tooltip="Texto explicativo sobre como e quando as horas de desenvolvimento serão executadas e faturadas."
                                >
                                    <TextArea
                                        rows={4}
                                        placeholder="Informe o detalhamento da contratação de horas..."
                                    />
                                </Form.Item>
                            )}
                        </Card>

                        {/* Bloco 3: Contatos */}
                        <Card
                            type="inner"
                            title={<><SolutionOutlined /> Contatos</>}
                            style={{ marginBottom: 20, borderRadius: '8px' }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="contato_principal_id"
                                        label={<><UserOutlined style={{ marginRight: 4 }} /> Contato Principal / Diretoria (Slide Final)</>}
                                        tooltip="Usuário que figurará como contato principal da diretoria no slide final. Por padrão, utiliza o definido nas Configurações Gerais."
                                    >
                                        <Select
                                            allowClear
                                            showSearch
                                            placeholder="Selecione o contato principal da empresa..."
                                            loading={loadingUsuarios || loadingConfigGeral}
                                            optionFilterProp="children"
                                            filterOption={(input, option: any) =>
                                                normalizarTexto(option?.children).includes(normalizarTexto(input))
                                            }
                                        >
                                            {usuarios.map(u => {
                                                const cargoDesc = typeof u.cargo === 'object' ? u.cargo?.nome : u.cargo;
                                                return (
                                                    <Option key={u.id} value={u.id}>
                                                        {u.nome}{cargoDesc ? ` (${cargoDesc})` : ''}
                                                    </Option>
                                                );
                                            })}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="vendedor_id"
                                        label={<><UserOutlined style={{ marginRight: 4 }} /> Consultor Comercial (Contato da Proposta)</>}
                                        rules={[{ required: true, message: 'Por favor, selecione o consultor da proposta' }]}
                                        tooltip="Selecione o usuário/consultor que constará no slide de contato e apresentação da proposta"
                                    >
                                        <Select
                                            showSearch
                                            placeholder="Selecione o consultor comercial..."
                                            loading={loadingUsuarios}
                                            optionFilterProp="children"
                                            filterOption={(input, option: any) =>
                                                normalizarTexto(option?.children).includes(normalizarTexto(input))
                                            }
                                        >
                                            {usuarios.map(u => {
                                                const cargoDesc = typeof u.cargo === 'object' ? u.cargo?.nome : u.cargo;
                                                return (
                                                    <Option key={u.id} value={u.id}>
                                                        {u.nome}{cargoDesc ? ` (${cargoDesc})` : ''}
                                                    </Option>
                                                );
                                            })}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* Coluna Direita: Resumo dos Valores Calculados */}
                    <Col xs={24} lg={8}>
                        <Card
                            type="inner"
                            title={
                                <Space>
                                    <DollarOutlined style={{ color: '#0066b2' }} />
                                    <span>Resumo da Proposta</span>
                                </Space>
                            }
                            style={{
                                background: isDark ? '#1f1f1f' : '#fafafa',
                                border: isDark ? '1px solid #303030' : '1px solid #d9d9d9',
                                borderRadius: '8px',
                                position: isMobile ? 'static' : 'sticky',
                                top: 20,
                                marginTop: isMobile ? 16 : 0,
                                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)'
                            }}
                        >
                            <div style={{ marginBottom: 14 }}>
                                <div
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        background: isDark ? '#262626' : '#fdfeff',
                                        border: isDark
                                            ? '1px solid #383838'
                                            : '1px solid #d6d6d6',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        color: isDark ? '#ffffff' : '#070707',

                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span>
                                        Plano Selecionado:
                                    </span>

                                    <span>
                                        {selectedConfigPlano?.plano?.nome ||
                                            selectedConfigPlano?.descricao_plano ||
                                            'Nenhum plano selecionado'}
                                    </span>
                                </div>
                            </div>

                            <Divider style={{ margin: '14px 0' }} />

                            <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
                                <Col>
                                    <Text>Licenças ({quantidadeLicencas}x de {formatarMoeda(valorUnitarioPlano)}):</Text>
                                </Col>
                                <Col>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {formatarMoeda(valorTotalLicencas)}
                                    </Text>
                                </Col>
                            </Row>

                            {contratacaoHoras && (
                                <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
                                    <Col>
                                        <Text>
                                            Horas ({quantidadeHoras}h x {formatarMoeda(valorUnitarioHora)}):
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Text strong style={{ fontSize: '14px' }}>
                                            {formatarMoeda(valorTotalHoras)}
                                        </Text>
                                    </Col>
                                </Row>
                            )}

                            {/* Card 1: Total Licenças */}
                            <div
                                style={{
                                    marginTop: 14,
                                    marginBottom: 10,
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    background: isDark ? '#11253e' : '#f0f7ff',
                                    border: isDark ? '1px solid #1d39c4' : '1px solid #bae0ff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                    <Text style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px', color: isDark ? '#85a5ff' : '#0958d9' }}>
                                        VALOR TOTAL LICENÇAS
                                    </Text>
                                    <Tag color="blue" style={{ fontSize: '10px', lineHeight: '18px', padding: '0 6px', margin: 0, borderRadius: '4px', fontWeight: 600 }}>
                                        FIXO / ANUAL
                                    </Tag>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <Text strong style={{ fontSize: '14px', color: isDark ? '#ffffff' : '#262626' }}>
                                        Total Licenças:
                                    </Text>
                                    <Text strong style={{ fontSize: '20px', color: isDark ? '#91caff' : '#0050b3', letterSpacing: '-0.3px' }}>
                                        {formatarMoeda(valorTotalLicencas)}
                                    </Text>
                                </div>
                                {condicaoPagamento === 'parcelado' && (
                                    <div
                                        style={{
                                            marginTop: 8,
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            background: isDark ? 'rgba(24, 144, 255, 0.14)' : '#ffffff',
                                            border: isDark ? '1px solid #177ddc' : '1px solid #91d5ff',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {/* Lado Esquerdo */}
                                        <Text strong style={{ fontSize: '13px', color: isDark ? '#d9d9d9' : '#1f1f1f' }}>
                                            Parcela Mensal:
                                        </Text>

                                        {/* Lado Direito com gap para controlar o espaço */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Text strong style={{ fontSize: '14px', color: isDark ? '#40a9ff' : '#0050b3' }}>
                                                {Number(numeroParcelas || 12)}x
                                            </Text>
                                            <Text strong style={{ fontSize: '16px', color: isDark ? '#40a9ff' : '#0050b3', letterSpacing: '-0.2px' }}>
                                                {formatarMoeda(valorParcela)}<span style={{ fontSize: '12px', fontWeight: 500, color: isDark ? '#8c8c8c' : '#595959' }}>/mês</span>
                                            </Text>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Card 2: Total Horas (Apenas se houver contratação de horas) */}
                            {contratacaoHoras && (
                                <div
                                    style={{
                                        marginBottom: 10,
                                        padding: '12px 14px',
                                        borderRadius: '8px',
                                        background: isDark ? '#2b2518' : '#fffbe6',
                                        border: isDark ? '1px solid #874d00' : '1px solid #ffe58f',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                        <Text style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px', color: isDark ? '#ffd591' : '#d46b08' }}>
                                            VALOR TOTAL POR HORA
                                        </Text>
                                        <Tag color="orange" style={{ fontSize: '10px', lineHeight: '18px', padding: '0 6px', margin: 0, borderRadius: '4px', fontWeight: 600 }}>
                                            SOB DEMANDA
                                        </Tag>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Text strong style={{ fontSize: '14px', color: isDark ? '#ffffff' : '#262626' }}>
                                            Total Horas:
                                        </Text>
                                        <Text strong style={{ fontSize: '20px', color: isDark ? '#ffa940' : '#d46b08', letterSpacing: '-0.3px' }}>
                                            {formatarMoeda(valorTotalHoras)}
                                        </Text>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: '11.5px', marginTop: 3, display: 'block', color: isDark ? '#8c8c8c' : '#595959' }}>
                                        ↳ Faturado sob demanda ({quantidadeHoras}h x {formatarMoeda(valorUnitarioHora)})
                                    </Text>
                                </div>
                            )}

                            {/* Card 4: Valor Total Global Estimado (quando houver contratação de horas) */}
                            {contratacaoHoras && quantidadeHoras > 0 && (
                                <div
                                    style={{
                                        marginBottom: 20,
                                        padding: '12px 14px',
                                        borderRadius: '8px',
                                        background: isDark ? '#262626' : '#fafafa',
                                        border: isDark ? '1px solid #383838' : '1px solid #d9d9d9',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                        <Text type="secondary" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px' }}>
                                            VALOR GLOBAL ESTIMADO DA PROPOSTA
                                        </Text>
                                        <Tag style={{ fontSize: '10px', lineHeight: '18px', padding: '0 6px', margin: 0, borderRadius: '4px' }}>
                                            TETO GLOBAL
                                        </Tag>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Text strong style={{ fontSize: '14px', color: isDark ? '#ffffff' : '#262626' }}>
                                            Total Geral:
                                        </Text>
                                        <Text strong style={{ fontSize: '20px', color: isDark ? '#ffffff' : '#141414', letterSpacing: '-0.3px' }}>
                                            {formatarMoeda(valorTotalGeral)}
                                        </Text>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: '11px', marginTop: 3, display: 'block' }}>
                                        Licenças ({formatarMoeda(valorTotalLicencas)}) + Horas ({formatarMoeda(valorTotalHoras)})
                                    </Text>
                                </div>
                            )}

                            <Button
                                icon={<FilePdfOutlined />}
                                size="large"
                                block
                                onClick={handleOpenPreview}
                                danger
                                style={{
                                    marginBottom: 12,
                                    height: 40,
                                    borderRadius: '6px',
                                    fontWeight: 500
                                }}
                            >
                                Pré-visualizar Proposta
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<FilePdfOutlined />}
                                size="large"
                                block
                                loading={submitting}
                                style={{
                                    height: 44,
                                    fontWeight: 700,
                                    fontSize: 15,
                                    borderRadius: '6px',
                                    background: '#0066b2',
                                    borderColor: '#0066b2',
                                    boxShadow: '0 2px 6px rgba(0, 102, 178, 0.3)'
                                }}
                            >
                                {isEdit ? 'Atualizar e Baixar Proposta (PDF)' : 'Salvar e Baixar Proposta (PDF)'}
                            </Button>
                        </Card>
                    </Col>
                </Row>
            </Form>

            {/* Modal de Pré-visualização Interativa dos Slides */}
            <VisualizadorPropostaModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                data={getPreviewData()}
                podeBaixarPdf={propostaSalva}
            />

            {/* Documento Oculto para Renderização e Download Automático do PDF ao Salvar */}
            <PropostaPrintDocument ref={printRef} data={propostaParaDownload || getPreviewData()} />
        </div>
    );
};

export default CadastrarProposta;
